import { useCallback, useEffect, useMemo, useState } from "react";
import {
	AlertTriangle,
	ChevronLeft,
	ChevronRight,
	Columns3,
	Download,
	FileSpreadsheet,
	Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { listAllTasks, type Task } from "@/services/task.service";
import { listProjects, type Project } from "@/services/project.service";
import { listSprints, type Sprint } from "@/services/sprint.service";
import { listProfiles, type Profile } from "@/services/profile.service";
import { downloadCSV } from "@/lib/csv";
import { exportTaskReportsXlsx } from "./exportTaskReportsXlsx";
import TaskReportsFilters, {
	type TaskReportsFilterValues,
} from "./TaskReportsFilters";
import TaskReportsTable from "./TaskReportsTable";
import ColumnSettingsModal from "./ColumnSettingsModal";
import {
	COLUMN_MAP,
	DEFAULT_COLUMNS,
	reconcileColumns,
	type ColumnState,
} from "./columns";

const PAGE_SIZES = [10, 25, 50, 100];

const COLUMNS_STORAGE_KEY = "taskReports.columns";

function loadColumns(): ColumnState[] {
	try {
		const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
		if (raw) return reconcileColumns(JSON.parse(raw) as ColumnState[]);
	} catch {
		/* ignore malformed storage */
	}
	return DEFAULT_COLUMNS;
}

const EMPTY_FILTERS: TaskReportsFilterValues = {
	projectId: "",
	sprintId: "",
	assignedTo: "",
	status: "",
	dateFrom: "",
	dateTo: "",
	search: "",
};

export default function TaskReportsTab() {
	const [filters, setFilters] = useState<TaskReportsFilterValues>(EMPTY_FILTERS);
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(25);

	const [tasks, setTasks] = useState<Task[]>([]);
	const [projects, setProjects] = useState<Project[]>([]);
	const [sprints, setSprints] = useState<Sprint[]>([]);
	const [users, setUsers] = useState<Profile[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const [columns, setColumns] = useState<ColumnState[]>(loadColumns);
	const [columnsOpen, setColumnsOpen] = useState(false);

	useEffect(() => {
		try {
			localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columns));
		} catch {
			/* ignore quota / unavailable storage */
		}
	}, [columns]);

	const visibleColumns = useMemo(
		() => columns.filter((c) => c.visible).map((c) => COLUMN_MAP[c.key]),
		[columns],
	);

	// Filter dropdown sources (load once).
	useEffect(() => {
		Promise.all([listProjects(), listSprints(), listProfiles()])
			.then(([p, s, u]) => {
				setProjects(p);
				setSprints(s);
				setUsers(u);
			})
			.catch(() => {
				/* dropdowns degrade gracefully to empty */
			});
	}, []);

	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(filters.search), 350);
		return () => clearTimeout(t);
	}, [filters.search]);

	// Reset to first page whenever any filter or page size changes.
	useEffect(() => {
		setPage(1);
	}, [
		filters.projectId,
		filters.sprintId,
		filters.assignedTo,
		filters.status,
		filters.dateFrom,
		filters.dateTo,
		debouncedSearch,
		limit,
	]);

	// Server filters: project / sprint / assignee / search. Status + created-date
	// range are applied client-side so the status dropdown stays populated.
	const load = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const data = await listAllTasks({
				project_id: filters.projectId || undefined,
				sprint_id: filters.sprintId || undefined,
				assigned_to: filters.assignedTo || undefined,
				search: debouncedSearch || undefined,
			});
			setTasks(data);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to load tasks",
			);
		} finally {
			setLoading(false);
		}
	}, [filters.projectId, filters.sprintId, filters.assignedTo, debouncedSearch]);

	useEffect(() => {
		load();
	}, [load]);

	const statusOptions = useMemo(
		() => Array.from(new Set(tasks.map((t) => t.status))).sort(),
		[tasks],
	);

	const filtered = useMemo(() => {
		const from = filters.dateFrom ? filters.dateFrom : null;
		// Include the whole "to" day.
		const to = filters.dateTo ? `${filters.dateTo}T23:59:59.999Z` : null;
		return tasks.filter((t) => {
			if (filters.status && t.status !== filters.status) return false;
			if (from && t.created_at < from) return false;
			if (to && t.created_at > to) return false;
			return true;
		});
	}, [tasks, filters.status, filters.dateFrom, filters.dateTo]);

	const total = filtered.length;
	const totalPages = Math.max(1, Math.ceil(total / limit));
	const pageItems = useMemo(
		() => filtered.slice((page - 1) * limit, page * limit),
		[filtered, page, limit],
	);

	const activeSprint = useMemo(
		() => sprints.find((s) => s.status === "active") ?? null,
		[sprints],
	);
	const selectedSprint = useMemo(
		() => sprints.find((s) => s.id === filters.sprintId) ?? null,
		[sprints, filters.sprintId],
	);
	const exportSprint = selectedSprint ?? activeSprint;
	const xlsxRows = useMemo(
		() =>
			selectedSprint
				? filtered
				: filtered.filter((t) => t.sprint_id === activeSprint?.id),
		[filtered, selectedSprint, activeSprint],
	);

	const patch = (p: Partial<TaskReportsFilterValues>) =>
		setFilters((prev) => ({ ...prev, ...p }));

	const handleExport = () => {
		const ctx = { projects };
		const rows = filtered.map((t) =>
			Object.fromEntries(
				visibleColumns.map((col) => [col.label, col.csv(t, ctx)]),
			),
		);
		downloadCSV("tasks-report", rows);
	};

	const handleExportXlsx = () => {
		if (!exportSprint) return;
		exportTaskReportsXlsx(xlsxRows, visibleColumns, { projects }, exportSprint);
	};

	return (
		<div className="space-y-4">
			<TaskReportsFilters
				values={filters}
				onChange={patch}
				onClear={() => setFilters(EMPTY_FILTERS)}
				projects={projects}
				sprints={sprints}
				users={users}
				statuses={statusOptions}
			/>

			<div className="flex items-center justify-between">
				<p className="text-xs text-muted">
					{loading ? "Loading…" : `${total} tasks`}
				</p>
				<div className="flex items-center gap-2">
					<Button
						size="sm"
						variant="outline"
						onClick={() => setColumnsOpen(true)}
					>
						<Columns3 className="h-4 w-4" />
						Change columns
					</Button>
					<Button
						size="sm"
						variant="outline"
						onClick={handleExport}
						disabled={loading || total === 0}
					>
						<Download className="h-4 w-4" />
						Export CSV
					</Button>
					<Button
						size="sm"
						variant="outline"
						onClick={handleExportXlsx}
						disabled={loading || !exportSprint || xlsxRows.length === 0}
					>
						<FileSpreadsheet className="h-4 w-4" />
						Export Excel
					</Button>
				</div>
			</div>

			{loading ? (
				<div className="flex items-center justify-center py-16 text-muted">
					<Loader2 className="h-5 w-5 animate-spin" />
				</div>
			) : error ? (
				<div className="flex items-start gap-2 rounded-md border border-danger/20 bg-danger-subtle p-3 text-sm text-danger">
					<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
					<span>{error}</span>
				</div>
			) : total === 0 ? (
				<p className="py-16 text-center text-sm text-muted">
					No tasks found.
				</p>
			) : (
				<>
					<TaskReportsTable
						tasks={pageItems}
						projects={projects}
						columns={visibleColumns}
					/>

					<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
						<div className="flex items-center gap-2 text-xs text-muted">
							<span>Rows per page</span>
							<select
								value={limit}
								onChange={(e) => setLimit(Number(e.target.value))}
								className="h-8 rounded-md border border-border bg-background px-2 text-foreground"
							>
								{PAGE_SIZES.map((n) => (
									<option key={n} value={n}>
										{n}
									</option>
								))}
							</select>
						</div>

						<div className="flex items-center gap-2 text-xs text-muted">
							<span>
								Page {page} of {totalPages}
							</span>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page <= 1}
							>
								<ChevronLeft className="h-4 w-4" />
								Prev
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() =>
									setPage((p) => Math.min(totalPages, p + 1))
								}
								disabled={page >= totalPages}
							>
								Next
								<ChevronRight className="h-4 w-4" />
							</Button>
						</div>
					</div>
				</>
			)}

			<ColumnSettingsModal
				open={columnsOpen}
				onOpenChange={setColumnsOpen}
				value={columns}
				onChange={setColumns}
			/>
		</div>
	);
}
